// @ts-nocheck
import { useState } from "react";
import {
  Select, Button, Modal, Form, Input, InputNumber,
  Typography, Space, Popconfirm, App, Tag, Table,
} from "../components/admin-ui";
import { PlusOutlined, EditOutlined, DeleteOutlined, CloseOutlined, TagsOutlined } from "../components/admin-icons";
import { useTranslation } from "react-i18next";
import { AdminShell } from "../layouts/AdminShell";
import { ImageUpload } from "../components/ImageUpload";
import {
  useAdminStalls,
  useAdminMenuCategories, useCreateMenuCategory, useUpdateMenuCategory, useDeleteMenuCategory,
  type MenuCategoryRow,
} from "../api/hooks";
const { Text } = Typography;

export function CategoriesScreen() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<MenuCategoryRow | null>(null);
  const [form] = Form.useForm();
  const [optionGroups, setOptionGroups] = useState<{ id: string; name: string; choices: { label: string; extra_charge: number; input_type: string }[]; max_select?: number }[]>([]);
  const [newChoiceDraft, setNewChoiceDraft] = useState<Record<string, { label?: string; price?: number; type?: string }>>({});

  const { data: stallsData } = useAdminStalls();
  const stallOptions = (stallsData?.data ?? []).map((s) => ({ value: s.id, label: s.name }));
  const stallNameMap = Object.fromEntries((stallsData?.data ?? []).map((s) => [s.id, s.name]));

  const { data: catData, isLoading } = useAdminMenuCategories();
  const categories: MenuCategoryRow[] = catData?.data ?? [];

  const createCat = useCreateMenuCategory();
  const updateCat = useUpdateMenuCategory();
  const deleteCat = useDeleteMenuCategory();

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ sort_order: categories.length });
    setOptionGroups([]);
    setNewChoiceDraft({});
    setModalOpen(true);
  };

  const openEdit = (c: MenuCategoryRow) => {
    setEditing(c);
    form.setFieldsValue({ name: c.name, emoji: c.emoji ?? undefined, icon_url: c.icon_url ?? undefined, sort_order: c.sort_order, stall_id: c.stall_id ?? undefined });
    setOptionGroups((c.default_option_groups ?? []).map((g) => ({ ...g, choices: g.choices.map((x) => (typeof x === "string" ? { label: x, extra_charge: 0, input_type: "radio" } : { label: x.label, extra_charge: Number(x.extra_charge) || 0, input_type: x.input_type ?? "radio" })) })));
    setNewChoiceDraft({});
    setModalOpen(true);
  };

  const addOptionGroup = () => {
    setOptionGroups([...optionGroups, { id: `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, name: "", choices: [] }]);
  };

  const removeOptionGroup = (groupId: string) => {
    setOptionGroups(optionGroups.filter((g) => g.id !== groupId));
  };

  const renameOptionGroup = (groupId: string, name: string) => {
    setOptionGroups(optionGroups.map((g) => (g.id === groupId ? { ...g, name } : g)));
  };

  const addChoice = (groupId: string) => {
    const d = newChoiceDraft[groupId] ?? {};
    const label = (d.label ?? "").trim();
    if (!label) return;
    const choice = { label, extra_charge: d.price ?? 0, input_type: d.type ?? "radio" };
    setOptionGroups(optionGroups.map((g) => (g.id === groupId && !g.choices.some((c) => c.label === label) ? { ...g, choices: [...g.choices, choice] } : g)));
    setNewChoiceDraft({ ...newChoiceDraft, [groupId]: { type: d.type } });
  };

  const patchChoice = (groupId: string, label: string, patch: Record<string, unknown>) => {
    setOptionGroups(optionGroups.map((g) => (g.id === groupId ? { ...g, choices: g.choices.map((c) => (c.label === label ? { ...c, ...patch } : c)) } : g)));
  };

  const removeChoice = (groupId: string, label: string) => {
    setOptionGroups(optionGroups.map((g) => (g.id === groupId ? { ...g, choices: g.choices.filter((c) => c.label !== label) } : g)));
  };

  const choiceTypeOptions = [
    { value: "radio", label: t("menu.choiceTypeRadio") },
    { value: "quantity", label: t("menu.choiceTypeQuantity") },
    { value: "input", label: t("menu.choiceTypeInputBox") },
    { value: "textarea", label: t("menu.choiceTypeTextarea") },
  ];

  const onSave = async () => {
    try {
      const vals = await form.validateFields();
      const groupsPayload = optionGroups.filter((g) => g.name.trim() && g.choices.length > 0);
      const payload = groupsPayload.length > 0 ? groupsPayload : null;
      if (editing) {
        await updateCat.mutateAsync({
          id: editing.id,
          name: vals.name,
          emoji: vals.emoji ?? null,
          icon_url: vals.icon_url ?? null,
          sort_order: vals.sort_order,
          stall_id: vals.stall_id ?? null,
          default_option_groups: payload,
        });
        void message.success(t("menu.categoryUpdateSuccess"));
      } else {
        await createCat.mutateAsync({
          name: vals.name,
          emoji: vals.emoji,
          icon_url: vals.icon_url ?? null,
          sort_order: vals.sort_order ?? 0,
          stall_id: vals.stall_id,
          default_option_groups: payload,
        });
        void message.success(t("menu.categoryCreateSuccess"));
      }
      setModalOpen(false);
    } catch {
      void message.error(t("menu.saveFailed"));
    }
  };

  const onDelete = async (c: MenuCategoryRow) => {
    try {
      await deleteCat.mutateAsync({ id: c.id });
      void message.success(t("menu.categoryDeleteSuccess"));
    } catch {
      void message.error(t("menu.deleteFailed"));
    }
  };

  return (
    <AdminShell>
      <div className="admin-categories-page admin-list-page">
        <div style={{ maxWidth: 1480, margin: "0 auto" }}>
          <div className="admin-categories-toolbar">
            <div>
              <h1 className="admin-page-title">
                {t("nav.categories")}
              </h1>
              <p className="admin-categories-count">{t("menu.categoryCount", { count: categories.length })}</p>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreate}
              className="admin-categories-create"
            >
              {t("menu.addCategory")}
            </Button>
          </div>

          <div className="admin-categories-table-panel admin-data-table-panel">
            {isLoading ? (
              <div className="admin-categories-loading">
                {t("common.loading")}
              </div>
            ) : (
              <Table
                rowKey="id"
                dataSource={categories}
                columns={[
                  {
                    title: t("menu.categoryEmoji"),
                    dataIndex: "emoji",
                    key: "emoji",
                    width: 80,
                    render: (emoji: string | null, c: MenuCategoryRow) =>
                      c.icon_url ? (
                        <img src={c.icon_url} alt="" className="admin-categories-icon-thumb" style={{ width: 28, height: 28, borderRadius: 8, objectFit: "cover" }} />
                      ) : emoji ? (
                        <span className="admin-categories-emoji">{emoji}</span>
                      ) : (
                        <Text type="secondary">—</Text>
                      ),
                  },
                  {
                    title: t("menu.categoryName"),
                    dataIndex: "name",
                    key: "name",
                    render: (name: string) => <Text strong className="admin-data-customer">{name}</Text>,
                  },
                  {
                    title: t("common.stall"),
                    dataIndex: "stall_id",
                    key: "stall",
                    width: 200,
                    render: (stall_id: string | null) =>
                      <span className="admin-categories-stall">{stall_id
                        ? <Tag color="orange">{stallNameMap[stall_id] ?? stall_id}</Tag>
                        : <Tag color="default">{t("menu.globalCategory")}</Tag>}</span>,
                  },
                  {
                    title: t("menu.categorySortOrder"),
                    dataIndex: "sort_order",
                    key: "sort_order",
                    width: 120,
                    defaultSortOrder: "ascend",
                    sorter: (a: MenuCategoryRow, b: MenuCategoryRow) =>
                      a.sort_order !== b.sort_order
                        ? a.sort_order - b.sort_order
                        : a.name.localeCompare(b.name),
                    render: (v: number) => <Text className="admin-data-time">{v}</Text>,
                  },
                  {
                    title: t("dashboard.colAction"),
                    key: "actions",
                    width: 144,
                    render: (_: unknown, c: MenuCategoryRow) => (
                      <Space>
                        <Button className="admin-categories-action admin-data-more" size="small" aria-label={t("common.edit")} icon={<EditOutlined />} onClick={() => openEdit(c)} />
                        <Popconfirm
                          title={c.name ? `${t("common.confirmDelete")} (${c.name})` : t("common.confirmDelete")}
                          onConfirm={() => void onDelete(c)}
                          okType="danger"
                        >
                          <Button className="admin-categories-action admin-categories-action-danger admin-data-more" size="small" danger aria-label={t("common.delete")} icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]}
              />
            )}
          </div>
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={<div className="admin-menu-category-heading">
          <span className="admin-menu-category-heading-icon" aria-hidden="true"><TagsOutlined /></span>
          <div><h2>{editing ? t("menu.categoryName") : t("menu.addCategory")}</h2><p>{t("nav.categories")}</p></div>
        </div>}
        onCancel={() => setModalOpen(false)}
        onOk={() => void onSave()}
        okText={t("common.save")}
        okButtonProps={{
          loading: createCat.isPending || updateCat.isPending,
        }}
      >
        <div className="admin-categories-form">
          <Form form={form} layout="vertical">
            <div className="admin-categories-fields">
              <Form.Item name="icon_url" label={t("menu.categoryIcon")} extra={t("menu.categoryIconHint")}>
                <ImageUpload folder="categories" />
              </Form.Item>
              <Form.Item name="emoji" label={t("menu.categoryEmoji")} extra={t("menu.categoryEmojiHint")}>
                <Input maxLength={8} />
              </Form.Item>
              <Form.Item name="name" label={t("menu.categoryName")} rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="sort_order" label={t("menu.categorySortOrder")}>
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                name="stall_id"
                label={t("common.stall")}
                extra={t("menu.categoryStallOptional")}
              >
                <Select allowClear options={stallOptions} placeholder={t("common.selectStall")} />
              </Form.Item>
            </div>
            <Form.Item
              className="admin-categories-options"
              label={t("menu.categoryOptionGroups", { defaultValue: "默认选项组" })}
              tooltip={t("menu.categoryOptionGroupsTooltip", { defaultValue: "新建此分类下的菜品时，会自动带上这些选项组（例如糖度、冰量、辣度等），可自行命名与增删。" })}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.5 }}>{t("menu.choiceTypeHelp")}</Text>
                {optionGroups.length === 0 && (
                  <Text type="secondary" style={{ fontSize: 12 }}>{t("menu.categoryOptionGroupsEmpty", { defaultValue: "暂无默认选项组" })}</Text>
                )}
                {optionGroups.map((group) => (
                  <div key={group.id} className="admin-categories-option-group">
                    <Space.Compact style={{ width: "100%", marginBottom: 8 }}>
                      <Input
                        value={group.name}
                        onChange={(e) => renameOptionGroup(group.id, e.target.value)}
                        placeholder={t("menu.categoryOptionGroupNamePlaceholder", { defaultValue: "选项组名称，如：辣度" })}
                        maxLength={30}
                      />
                      <Button className="admin-categories-remove-group" danger aria-label={t("common.delete")} icon={<DeleteOutlined />} onClick={() => removeOptionGroup(group.id)} />
                    </Space.Compact>
                    {group.choices.map((choice) => (
                      <Space.Compact key={choice.label} style={{ width: "100%", marginBottom: 6 }}>
                        <Input value={choice.label} readOnly style={{ flex: 1 }} />
                        <InputNumber
                          prefix="RM" min={0} max={9999.99} precision={2} value={choice.extra_charge}
                          onChange={(v) => patchChoice(group.id, choice.label, { extra_charge: v ?? 0 })}
                          style={{ width: 120 }}
                        />
                        <Select value={choice.input_type} options={choiceTypeOptions} style={{ width: 130 }}
                          onChange={(v) => patchChoice(group.id, choice.label, { input_type: v })} />
                        <Button danger aria-label={t("common.delete")} icon={<CloseOutlined />} onClick={() => removeChoice(group.id, choice.label)} />
                      </Space.Compact>
                    ))}
                    <Space.Compact style={{ width: "100%" }}>
                      <Input
                        value={newChoiceDraft[group.id]?.label ?? ""}
                        onChange={(e) => setNewChoiceDraft({ ...newChoiceDraft, [group.id]: { ...newChoiceDraft[group.id], label: e.target.value } })}
                        onPressEnter={() => addChoice(group.id)}
                        placeholder={t("menu.categoryOptionChoicePlaceholder", { defaultValue: "如：火腿" })}
                        maxLength={50}
                      />
                      <InputNumber
                        prefix="RM" min={0} max={9999.99} precision={2} value={newChoiceDraft[group.id]?.price}
                        onChange={(v) => setNewChoiceDraft({ ...newChoiceDraft, [group.id]: { ...newChoiceDraft[group.id], price: v ?? undefined } })}
                        style={{ width: 120 }}
                      />
                      <Select value={newChoiceDraft[group.id]?.type ?? "radio"} options={choiceTypeOptions} style={{ width: 130 }}
                        onChange={(v) => setNewChoiceDraft({ ...newChoiceDraft, [group.id]: { ...newChoiceDraft[group.id], type: v } })} />
                      <Button icon={<PlusOutlined />} onClick={() => addChoice(group.id)}>
                        {t("settings.addBtn")}
                      </Button>
                    </Space.Compact>
                  </div>
                ))}
                <Button className="admin-categories-add-group" icon={<PlusOutlined />} onClick={addOptionGroup} style={{ width: "100%" }}>
                  {t("menu.categoryAddOptionGroup", { defaultValue: "添加选项组" })}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </AdminShell>
  );
}
